import { useQuery } from "@tanstack/react-query";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { Settings as SettingsIcon, ShieldCheck, Sparkles } from "lucide-react";
import { GovernancePage } from "./pages/GovernancePage";
import { LoginPage } from "./pages/LoginPage";
import { SettingsPage } from "./pages/SettingsPage";
import { LicenseBadge } from "./components/LicenseBadge";
import { TourLauncher, TourOverlay, tourForPath, useAutoStartTour } from "./components/tour";
import { platformApi, type ConfigResponse } from "./lib/api";

export function App() {
  return (
    <div className="grid min-h-screen grid-rows-[auto,1fr] bg-atlas-fog">
      <Header />
      <main className="min-h-0">
        <Routes>
          <Route path="/" element={<Navigate to="/governance" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/governance" element={<GovernancePage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </main>
      <RouteTourBinder />
      <TourOverlay />
    </div>
  );
}

function RouteTourBinder() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname);
  if (!tour) return null;
  return <BoundTour key={tour.id} />;
}

function BoundTour() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname)!;
  useAutoStartTour(tour);
  return null;
}

function Header() {
  const { data: config } = useQuery({
    queryKey: ["atlas-config"],
    queryFn: async () => (await platformApi.get<ConfigResponse>("/config")).data,
    staleTime: 60_000,
    retry: false,
  });
  return (
    <header className="sticky top-0 z-20 border-b border-atlas-slate/25 bg-white/85 backdrop-blur">
      <div className="flex w-full items-center gap-6 px-6 py-2.5">
        <a href="/" className="flex items-center gap-2.5 font-semibold text-atlas-ink">
          <Logo />
          <div className="leading-tight">
            <div className="font-display text-[15px]">DatumCraft Atlas</div>
            <div className="text-2xs font-normal text-atlas-slate">
              Cross-platform BI governance + compliance
            </div>
          </div>
        </a>
        <nav aria-label="primary" data-tour-id="nav" className="flex gap-1 text-sm">
          <NavItem to="/governance" icon={<ShieldCheck size={14} />}>Governance</NavItem>
          <NavItem to="/settings" icon={<SettingsIcon size={14} />}>Settings</NavItem>
        </nav>
        <div className="ml-auto flex items-center gap-2 text-2xs text-atlas-slate">
          {config?.ruleCount != null && config.ruleCount > 0 && (
            <span
              title={`${config.ruleCount} governance rules loaded`}
              className="inline-flex items-center gap-1 rounded-full bg-atlas-primary/10 px-2 py-0.5 font-medium text-atlas-primary"
            >
              <ShieldCheck size={11} /> {config.ruleCount} rules
            </span>
          )}
          {config?.llmProvider && config.llmProvider !== "fallback" && (
            <span
              title={`LLM provider — ${config.llmProvider}`}
              className="inline-flex items-center gap-1 rounded-full bg-atlas-accent/10 px-2 py-0.5 font-medium text-atlas-primary"
            >
              <Sparkles size={11} /> {config.llmProvider}
            </span>
          )}
          <LicenseBadge />
          <TourLauncher />
        </div>
      </div>
    </header>
  );
}

function NavItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition-colors ${
          isActive
            ? "bg-atlas-primary text-white shadow-sm"
            : "text-atlas-ink hover:bg-atlas-mist"
        }`
      }
    >
      <span aria-hidden>{icon}</span>
      <span>{children}</span>
    </NavLink>
  );
}

// Product glyph — 4-platform graph with a central indigo hub (the
// unified governance view). Mirrors `GlyphAtlas` from the website's
// brand system at `website/app/brand/products/glyphs.tsx`. Outline +
// nodes use currentColor so they follow the parent text-* class;
// the central hub is brand indigo #4B6BFB.
function Logo() {
  const nodes: Array<[number, number]> = [
    [12, 3.5],
    [20.5, 12],
    [12, 20.5],
    [3.5, 12],
  ];
  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-atlas-ink text-white shadow-sm"
      aria-hidden
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {nodes.map(([x, y], i) => (
          <line
            key={i}
            x1="12"
            y1="12"
            x2={x}
            y2={y}
            stroke="currentColor"
            strokeWidth={1.6}
            opacity="0.55"
          />
        ))}
        {nodes.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="1.5" fill="currentColor" />
        ))}
        <circle cx="12" cy="12" r="2.4" fill="#4B6BFB" />
      </svg>
    </span>
  );
}
